import React from "react";
import { Link } from "react-router-dom";
import { Award, CheckCircle2, ArrowRight, Globe, Cpu } from "lucide-react";
import { PageHero } from "./Universita";
import { UNIVERSITIES, EIPASS_CERTS, LOGOS } from "../data/universities";
import { getCourseImage } from "../data/courseImages";

export default function MasterPage() {
  const allMasters = UNIVERSITIES.flatMap((u) => u.master.map((m) => ({ name: m, uni: u.short, color: u.color, uniId: u.id, logo: u.logo })));

  return (
    <div data-testid="page-master">
      <PageHero
        eyebrow="Master di I° e II° livello"
        title="Master, CIAK & certificazioni."
        sub="Specializzazioni post-laurea per crescere in carriera. Master direttamente collegati con il mondo del lavoro."
      />

      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-brand-navy">I master del Polo</h2>
            <div className="text-sm text-brand-muted">{allMasters.length} master disponibili</div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="master-grid">
            {allMasters.map((m) => (
              <div key={`${m.uni}-${m.name}`} className="lift-card group rounded-2xl bg-white border border-brand-line overflow-hidden flex flex-col">
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={getCourseImage(m.uniId, m.name)}
                    alt={m.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/75 via-brand-navy/20 to-transparent" />
                  <div className="absolute top-3 left-3 right-3 flex items-center justify-between gap-2">
                    <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-1 rounded-full bg-amber-100/95 text-amber-900">
                      Master
                    </span>
                    <div className="bg-white/95 rounded-full px-2 py-1 flex items-center">
                      <img src={m.logo} alt={m.uni} className="h-4 max-w-[70px] object-contain" />
                    </div>
                  </div>
                  <div className="absolute bottom-3 left-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md" style={{ background: m.color }}>
                      <Award className="w-5 h-5" />
                    </div>
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-heading font-bold text-lg text-brand-navy leading-tight">{m.name}</h3>
                  <p className="text-sm text-brand-muted mt-2">1500h · 60 CFU · Modalità online</p>
                  <div className="mt-auto pt-5 border-t border-brand-line flex items-center justify-between">
                    <span className="text-sm text-brand-muted">Retta agevolata</span>
                    <Link to="/contatti" state={{ uni: m.uniId, corso: m.name }} className="text-brand-emerald font-bold text-sm inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid={`master-cta-${m.uniId}`}>
                      Info <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EIPASS */}
      <section className="py-16 lg:py-24 bg-brand-surface">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow text-brand-emerald mb-3">Certificazioni Incluse</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-brand-navy leading-[1.05]">
              EIPASS: digitale + inglese, riconosciuto MIUR.
            </h2>
            <p className="mt-4 text-brand-muted text-lg">
              Certificazioni informatiche e linguistiche valide per concorsi pubblici, graduatorie e selezioni aziendali.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {EIPASS_CERTS.map((c, i) => (
              <div key={c.title} className="rounded-3xl bg-white border border-brand-line p-8 lift-card" data-testid={`eipass-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-brand-navy text-white flex items-center justify-center mb-5">
                  {i === 0 ? <Cpu className="w-5 h-5" /> : <Globe className="w-5 h-5" />}
                </div>
                <h3 className="font-heading font-extrabold text-2xl text-brand-navy">{c.title}</h3>
                <div className="text-brand-emerald font-bold text-sm mt-1">{c.subtitle}</div>
                <p className="mt-4 text-brand-muted leading-relaxed">{c.desc}</p>
                <ul className="mt-5 space-y-2">
                  {c.points.map((p) => (
                    <li key={p} className="flex gap-2 text-sm text-brand-ink">
                      <CheckCircle2 className="w-4 h-4 text-brand-emerald mt-0.5 shrink-0" /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-3xl bg-brand-navy text-white p-8 lg:p-10 grid lg:grid-cols-2 gap-6 items-center">
            <div>
              <h3 className="font-heading font-extrabold text-2xl">Benefici delle certificazioni EIPASS</h3>
              <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm">
                {["Qualifica il CV", "Crediti formativi", "Punteggi concorsi pubblici", "Accesso più rapido al lavoro"].map((b) => (
                  <li key={b} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-brand-emerald shrink-0 mt-0.5" /> {b}</li>
                ))}
              </ul>
            </div>
            <div className="flex justify-end">
              <Link to="/contatti" className="btn-primary" data-testid="eipass-cta">
                Scopri i Master <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
